import { useContext, useState } from "react";
import { Box } from "@mui/material";
import { ParagraphText } from "../../../../Libraries";
import moment from "moment";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { ImgPlaceHolder, RupinFavIcon } from "../../../../assets/icons/icon";
import AddContactSelector from "../../../common/AddContactSelector";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { LanguageContext } from "../../../../context/LanguageContext";
import styles from "./TransactionListing.module.css";

const TYPE_LABEL_KEYS = {
  cashback: "cashback_label",
  bill_payment: "bill_payment_label",
  recharge: "recharge",
  voucher_payment: "voucher",
};

const getTypeLabel = (type, I18n) => {
  if (!type) return "-";
  const key = TYPE_LABEL_KEYS[type];
  if (key && I18n?.[key]) return I18n[key];
  return type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const TransactionRow = ({
  item,
  onRowClick,
  contacts = [],
  onContactSelected,
}) => {
  const [I18n] = useContext(LanguageContext);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const {
    created_at,
    bill_company,
    billing_company,
    logo_link,
    txn_sub_type,
    consumer_number_masked,
    consumer_number,
    extra_info,
    consumer_name,
    customer_name,
    amount,
    mode,
    service: billService,
  } = item || {};

  const isCashback = mode === "credit";
  const companyName = billService || bill_company || billing_company || "";
  const isCredit = mode === "credit";

  // Cashback: service key = company name, extra_info string = consumer number
  // Bill payment: companyName = company, consumer_number field = consumer number
  const service = companyName;
  const rawConsumerNum = isCashback
    ? extra_info || ""
    : consumer_number || consumer_number_masked || "";
  const consumerNumDisplay =
    [service, rawConsumerNum].filter(Boolean).join(" ") || "-";

  return (
    <Box
      id={`transaction_listing_row_${item.id}`}
      className={`invoiceListTableBody accountTableHeaderMain${!isCashback ? " pointer" : ""}`}
      onClick={
        !isCashback
          ? () => {
              triggerLogEvent(analytics, "w_bill_txn_transaction_detail");
              onRowClick?.(item);
            }
          : undefined
      }
    >
      {/* Date */}
      <Box className="invListTableHeaderCol sixColumn textLeft">
        <ParagraphText className="fontSize14 fontWeight400 newFontColor">
          {created_at ? moment(created_at).format("DD MMM YYYY") : "-"}
        </ParagraphText>
      </Box>

      {/* Company — cashback always shows RUPIN, bill payment shows actual company */}
      <Box
        className={`invListTableHeaderCol sixColumn textLeft ${styles.contactCell}`}
      >
        {isCashback ? (
          <Box className={styles.companyLogoFallback}>
            <RupinFavIcon width={20} height={20} />
          </Box>
        ) : logo_link ? (
          <img
            src={logo_link}
            alt={companyName}
            className={styles.companyLogo}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : (
          <Box className={styles.companyLogoFallback}>
            <ImgPlaceHolder width={20} height={20} />
          </Box>
        )}
        <ParagraphText className="fontSize14 fontWeight400 truncateOneLine newFontColor">
          {isCashback ? "RUPIN" : companyName || "-"}
        </ParagraphText>
      </Box>

      {/* Type */}
      <Box className="invListTableHeaderCol sixColumn textLeft">
        <ParagraphText className="fontSize14 fontWeight400 newFontColor">
          {txn_sub_type
            ? getTypeLabel(txn_sub_type, I18n)
            : isCashback
              ? I18n.cashback_label
              : I18n.bill_payment_label}
        </ParagraphText>
      </Box>

      {/* Consumer number — format: "K Electric 032435435433" */}
      <Box className="invListTableHeaderCol sixColumn textLeft">
        <ParagraphText className="fontSize14 fontWeight400 newFontColor truncateOneLine">
          {consumerNumDisplay}
        </ParagraphText>
      </Box>

      {/* Consumer name — cashback: consumer_name only; bill: consumer_name • contact_name OR consumer_name • Add contact */}
      <Box
        className={`invListTableHeaderCol sixColumn textLeft ${styles.contactCell}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isCashback ? (
          <ParagraphText className="fontSize14 fontWeight400 newFontColor">
            {consumer_name || "-"}
          </ParagraphText>
        ) : (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {consumer_name && (
              <>
                <Box style={{ flex: "0 1 auto", minWidth: 0, overflow: "hidden" }}>
                  <ParagraphText className="fontSize14 fontWeight400 newFontColor truncateOneLine">
                    {consumer_name}
                  </ParagraphText>
                </Box>
                <span className="fontSize14 newFontColor" style={{ flexShrink: 0, margin: "0 6px" }}>
                  •
                </span>
              </>
            )}
            {customer_name ? (
              <ParagraphText
                className="fontSize14 fontWeight400 newFontColor truncateOneLine"
                style={{ flexShrink: 0, maxWidth: "55%", overflow: "hidden" }}
              >
                {customer_name}
              </ParagraphText>
            ) : (
              <Box style={{ flexShrink: 0, width: "fit-content" }}>
                <AddContactSelector
                  contacts={contacts}
                  isOpen={selectorOpen}
                  onOpenChange={setSelectorOpen}
                  isPhoneRequired={true}
                  hideTriggerIcon={true}
                  addContactLabel={I18n.add_contact}
                  onSelect={(contact) => {
                    setSelectorOpen(false);
                    onContactSelected?.(contact, item);
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Amount */}
      <Box className="invListTableHeaderCol sixColumn textRight">
        <ParagraphText
          className={`fontSize14 fontWeight400 ${isCredit ? styles.creditAmount : styles.debitAmount}`}
        >
          {isCredit ? "+" : "-"}Rs. {formatNum(Math.abs(amount || 0))}
        </ParagraphText>
      </Box>
    </Box>
  );
};

export default TransactionRow;
